import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ComponentStatus, StateComponent } from '../../../../../common/components/state/state.component';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBettingResultModel, IFormModel } from '../../../../../common/apis/form/model/form.model';
import { UtilsService } from '../../../../../common/services/utils.service';
import { CompetitionGroupCommonService } from '../common/services/competition-group-common.service';
import { AnalyticsService } from '../../../../../common/services/analytics.service';
import { FormBettingApiService } from '../../../../../common/apis/form/form-betting-api.service';
import { NamesMetadataMapping } from './common/model/model';
import { BetStatisticsComponent } from './common/components/bet-statistics/bet-statistics.component';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem, MatListItemIcon, MatListItemLine, MatListItemTitle } from '@angular/material/list';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { BetResultRendererComponent } from './common/components/bet-result-renderer/bet-result-renderer.component';
import { KeyValuePipe } from '@angular/common';
import { MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle } from '@angular/material/card';
import { LoadingContainerComponent } from '../../../../../common/components/loading-container/loading-container.component';
import { NavBackComponent } from '../../../../../common/components/main/app-bar/nav-back/nav-back.component';
import { MatButton } from '@angular/material/button';
import { SimulateResultsStateService } from '../simulate-results-form-filler/simulate-results-state.service';

@Component({
  selector: 'oo-form-results',
  templateUrl: './form-results.component.html',
  styleUrls: ['./form-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NavBackComponent,
    LoadingContainerComponent,
    MatButton,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatCardContent,
    MatCardActions,
    BetResultRendererComponent,
    MatTabGroup,
    MatTab,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatList,
    MatListItem,
    MatIcon,
    MatListItemIcon,
    MatListItemTitle,
    MatListItemLine,
    BetStatisticsComponent,
    KeyValuePipe,
  ],
})
export class FormResultsComponent extends StateComponent<FormResultsComponentState> implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly formApi = inject(FormBettingApiService);
  private readonly compGroupCommonService = inject(CompetitionGroupCommonService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly simulateResultsStateService = inject(SimulateResultsStateService);

  state: FormResultsComponentState = {
    status: 'ready',
    form: null!,
    compGroupId: this.route.snapshot.paramMap.get('compGroupId')!,
    userBettingResults: null!,
    originalUserBettingResults: null!,
    metadata: null!,
    areBettingsSet: false,
    simulatedResults: undefined,
    originalForm: null!,
  };

  ngOnInit(): void {
    this.analyticsService.trackScreenName('formResults');
    this.fetchData();
  }

  keyToText(key: string): string {
    return UtilsService.camelToText(key, true);
  }

  startResultsSimulationFlow(): void {
    this.router.navigate([`../../simulateResults/${this.state.form.id}`], {
      relativeTo: this.route,
    });
  }

  clearSimulatedResults(): void {
    this.simulateResultsStateService.clearSimulatedResults(this.state.compGroupId, this.state.form.id);
    this.updateState({
      userBettingResults: this.state.originalUserBettingResults,
      form: this.state.originalForm,
    });
  }

  private fetchData(): void {
    this.updateStatus('loading');
    Promise.all([
      this.formApi.getFormById(this.state.compGroupId, this.route.snapshot!.paramMap.get('formId')!,
        ['metadata', 'bettingResult', 'config', 'bettingResult', 'hasBettingResult']),
      this.formApi.getFormResults(this.state.compGroupId, this.route.snapshot!.paramMap.get('formId')!),
    ]).then(([form, userBettingResults]) => {
      const userBetting = this.sortBettingResults(userBettingResults);
      const simulatedData = this.simulateResultsStateService.getSimulateResults(this.state.compGroupId, form.id);
      const simulatedBettingResults = simulatedData?.bettingResults && this.sortBettingResults(simulatedData?.bettingResults);
      const simulatedForm = {
        ...form,
        bettingResult: simulatedData?.result,
      };

      this.updateState({
        status: 'ready',
        userBettingResults: simulatedBettingResults ?? userBetting,
        originalUserBettingResults: [...userBetting],
        form: simulatedBettingResults ? simulatedForm : form,
        metadata: this.compGroupCommonService.flattenMetadata(form),
        areBettingsSet: form.hasBettingResult,
        simulatedResults: simulatedBettingResults,
        originalForm: form,
      });
    })
      .catch((e: Error) => {
        console.error(e);
        this.updateStatus('error');
      });
  }

  private sortBettingResults(results: FormBettingResultModel[]): FormBettingResultModel[] {
    return results.sort((a, b) => 
      a.firstName.toLocaleLowerCase() < b.firstName.toLocaleLowerCase() ? -1 : 1
    );
  }
}

interface FormResultsComponentState {
  status: ComponentStatus;
  compGroupId: string;
  form: IFormModel;
  userBettingResults: FormBettingResultModel[];
  metadata: NamesMetadataMapping;
  areBettingsSet: boolean;
  simulatedResults: FormBettingResultModel[] | undefined;
  originalUserBettingResults: FormBettingResultModel[];
  originalForm: IFormModel;
}
